import React from "react";
import classNames from "classnames";
import { useMatchMedia } from "../../utils/useMatchMedia";
import { Cta } from "../cta";
import textToUrlPath from "../../utils/textToUrlPath";

function BlankSectionNotSpacing({ children, anchorTag, bgClass }) {
  return (
    <section className={bgClass} id={anchorTag}>
      <div className="px-4 py-0 mx-auto max-w-7xl lg:px-8">{children}</div>
    </section>
  );
}

function BlankSection({ children, anchorTag, bgClass }) {
  return (
    <section className={bgClass} id={anchorTag}>
      <div className="py-8 px-4 mx-auto max-w-7xl sm:py-16 lg:px-8 xl:py-32">
        {children}
      </div>
    </section>
  );
}

function BlankSectionNotBoxed({ children, anchorTag, bgClass }) {
  return (
    <section className={bgClass} id={anchorTag}>
      <div className="py-8 sm:py-16 xl:py-32">{children}</div>
    </section>
  );
}

function SectionWithHeading({ heading, bgClass, anchorTag, children }) {
  anchorTag = anchorTag || textToUrlPath(heading);

  return (
    <BlankSection bgClass={bgClass} anchorTag={anchorTag}>
      <div className="text-center mb-12 lg:mb-24">
        <h2 className="font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 ">
          {heading}
        </h2>
      </div>
      <div>{children}</div>
    </BlankSection>
  );
}

function SectionWithHeadingForStats({ heading, bgClass, anchorTag, children }) {
  anchorTag = anchorTag || textToUrlPath(heading);
  return (
    <section className={bgClass} id={anchorTag}>
      <div className="max-w-7xl mx-auto py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="font-bold text-3xl lg:text-4xl tracking-tight text-gray-900">
            {heading}
          </h2>
        </div>
      </div>
      <>{children}</>
    </section>
  );
}

function SectionWithHeadingAndDescription({
  heading,
  description,
  shortdescription,
  bgClass,
  anchorTag,
  children,
}) {
  anchorTag = anchorTag || textToUrlPath(heading);
  return (
    <>
      <BlankSection bgClass={bgClass} anchorTag={anchorTag}>
        <div className="text-center mb-5">
          <h2 className="font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 ">
            {heading}
          </h2>
        </div>
        <div className="text-center mb-12">
          {description && (
            <p className="font-normal text-xl text-gray-500 mb-10">
              {description}
            </p>
          )}
          {shortdescription && (
            <p
              className="font-normal text-xl text-gray-500 mb-10"
              dangerouslySetInnerHTML={{
                __html: shortdescription,
              }}
            />
          )}
        </div>
        <div>{children}</div>
      </BlankSection>
    </>
  );
}

function SectionWithHeadingDescriptionAndCTA({
  heading,
  description,
  ctaText,
  ctaLink,
  bgClass,
  anchorTag,
  children,
}) {
  anchorTag = anchorTag || textToUrlPath(heading);
  return (
    <SectionWithHeadingAndDescription
      bgClass={bgClass}
      heading={heading}
      description={description}
      anchorTag={anchorTag}
    >
      {children}
      <Cta ctaText={ctaText} ctaLink={ctaLink} />
    </SectionWithHeadingAndDescription>
  );
}

function SectionWithHeadingAndCTA({
  heading,
  ctaText,
  ctaLink,
  bgClass,
  anchorTag,
  children,
}) {
  anchorTag = anchorTag || textToUrlPath(heading);
  return (
    <SectionWithHeading
      bgClass={bgClass}
      heading={heading}
      anchorTag={anchorTag}
    >
      {children}
      <Cta ctaText={ctaText} ctaLink={ctaLink} />
    </SectionWithHeading>
  );
}

function SectionWithCTASmallScreen({ ctaText, ctaLink, bgClass, children }) {
  const isDesktopResolution = useMatchMedia("(min-width:768px)", true);

  return (
    <BlankSection bgClass={bgClass}>
      {children}
      {!isDesktopResolution && <Cta ctaText={ctaText} ctaLink={ctaLink} />}
    </BlankSection>
  );
}

/**
 * Dev note: Need to work on this for company > customers page
 * @param {*} param0
 * @returns
 */
function SectionWithHeadingTabsAndCTA({
  heading,
  ctaText,
  ctaLink,
  anchorTag,
  bgClass,
  tabs,
  children,
}) {
  anchorTag = anchorTag || textToUrlPath(heading);
  return (
    <SectionWithHeading bgClass={bgClass} heading={heading}>
      <div>
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">
            Select a tab
          </label>
          <select
            id="tabs"
            name="tabs"
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            defaultValue={tabs.find((tab) => tab.current).name}
          >
            {tabs.map((tab) => (
              <option key={tab.name}>{tab.name}</option>
            ))}
          </select>
        </div>
        <div className="hidden sm:block">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => (
                <a
                  key={tab.name}
                  href={tab.href}
                  className={classNames(
                    tab.current
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                    "whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                  )}
                  aria-current={tab.current ? "page" : undefined}
                >
                  {tab.name}
                </a>
              ))}
            </nav>
          </div>
        </div>
      </div>
      <Cta ctaLink={ctaLink} ctaText={ctaText} />
    </SectionWithHeading>
  );
}

function SubSectionWithHeadingAndDescription({
  heading,
  description,
  shortdescription,
  bgClass,
  // anchorTag,
  children,
}) {
  // anchorTag = anchorTag || textToUrlPath(heading);
  return (
    <>
      <div className="py-8 sm:py-16 xl:py-16">
        <div className="text-center">
          <h2 className="font-bold text-2xl lg:text-2xl tracking-tight text-gray-900 ">
            {heading}
          </h2>
        </div>
        <div className="text-center mb-12">
          {description && (
            <p className="font-normal text-xl text-gray-500 mb-10">
              {description}
            </p>
          )}
          {shortdescription && (
            <p
              className="font-normal text-xl text-gray-500 mb-10"
              dangerouslySetInnerHTML={{
                __html: shortdescription,
              }}
            />
          )}
        </div>
        <div>{children}</div>
      </div>
    </>
  );
}

export {
  BlankSectionNotSpacing,
  BlankSection,
  BlankSectionNotBoxed,
  SectionWithHeading,
  SectionWithHeadingAndDescription,
  SectionWithHeadingDescriptionAndCTA,
  SectionWithHeadingAndCTA,
  SectionWithCTASmallScreen,
  SectionWithHeadingTabsAndCTA,
  SectionWithHeadingForStats,
  SubSectionWithHeadingAndDescription
};
